/**
 * Schlanker fetch-Wrapper mit:
 *   - automatischer JSON-(De)Serialisierung
 *   - Cookies (credentials: 'include') für Session-Auth
 *   - einheitlichem Error-Handling (mappt Response-JSON auf ApiError)
 *   - Typ-Inferenz über Response-Shape { data, meta? }
 */

export interface ApiResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE_URL = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

/**
 * Hook für 401-Handling. Wird von AuthProvider registriert und setzt bei
 * Session-Ablauf den User zurück + leitet auf /login um.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** Pfade, bei denen ein 401 erwartet ist und KEIN Redirect ausgelöst werden soll. */
const AUTH_CHECK_PATHS = new Set(['/auth/me', '/auth/login']);

/**
 * Grace-Period nach erfolgreichem Login. Während dieser Zeit werden 401s
 * ignoriert — sie entstehen fast immer durch bereits laufende Queries mit
 * altem Cookie, während der Browser den neuen Session-Cookie noch nicht
 * appliziert hat. Nach dem Fenster sind 401s echt.
 */
let loginGraceUntil = 0;
export function markFreshLogin(graceMs = 3000): void {
  loginGraceUntil = Date.now() + graceMs;
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  // Wenn BASE_URL relativ ("/api") ist, wird URL gegen window.location gebaut — wir
  // liefern nur Pfad+Query zurück, damit der Proxy in Vite weiterhin funktioniert.
  return url.pathname + url.search;
}

/**
 * Liest das CSRF-Cookie und gibt den Token zurück (null wenn nicht gesetzt).
 * Das Cookie wird vom Backend beim ersten Request ausgestellt.
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : null;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  query?: Query,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  // CSRF-Token nur bei mutierenden Methoden mitschicken
  const isMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  if (isMutating) {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const url = buildUrl(path, query);
  const fetchOpts: RequestInit = {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  let res = await fetch(url, fetchOpts);

  // Auto-Retry bei 401 während Login-Grace-Period: die Race-Condition
  // entsteht durch gleichzeitige Requests direkt nach Login, wenn der
  // Browser den neuen Session-Cookie noch nicht auf alle laufenden
  // Connections angewandt hat. 1× kurz warten + erneuter Versuch.
  if (res.status === 401 && Date.now() < loginGraceUntil && !AUTH_CHECK_PATHS.has(path)) {
    await new Promise((r) => setTimeout(r, 250));
    res = await fetch(url, fetchOpts);
  }

  // 204 No Content
  if (res.status === 204) return { data: undefined as unknown as T };

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, 'INVALID_RESPONSE', `Ungültige Server-Antwort (${res.status})`);
  }

  if (!res.ok) {
    const err = (json as { error?: ApiErrorPayload })?.error ?? {
      code: 'UNKNOWN',
      message: 'Unbekannter Fehler',
    };
    // 401-Handling: ein einzelner 401 in einem Widget darf NICHT sofort den
    // User ausloggen — das hat Arbeit zerrissen (Formulareingaben weg).
    // Stattdessen verifizieren wir über /auth/me ob die Session wirklich
    // abgelaufen ist. Nur dann rufen wir onUnauthorized() auf.
    //
    // Normale Query-401s (wegen Backend-Neustart, Prisma-Pool-Artefakt,
    // kurzer Cookie-Race) werden als lokaler Query-Error geworfen und
    // können mit React-Query-Retry + UI-Fehleranzeige behandelt werden.
    if (
      res.status === 401 &&
      !AUTH_CHECK_PATHS.has(path) &&
      Date.now() >= loginGraceUntil &&
      onUnauthorized
    ) {
      void verifyAuthThenLogout();
    }
    throw new ApiError(res.status, err.code, err.message, err.details);
  }

  return json as ApiResponse<T>;
}

/**
 * Prüft über /auth/me ob der Server uns wirklich als unauthentifiziert sieht.
 * Nur dann loggen wir den User aus (zurück zum Login). Dadurch überleben
 * Widgets einzelne 401s (z.B. nach Backend-Neustart im Dev), ohne dass der
 * User seine Arbeit verliert.
 *
 * Debounced: mehrere parallele 401s triggern nur einen /me-Check.
 */
let verifyInFlight: Promise<void> | null = null;
async function verifyAuthThenLogout(): Promise<void> {
  if (verifyInFlight) return verifyInFlight;
  verifyInFlight = (async () => {
    try {
      const res = await fetch(buildUrl('/auth/me'), { credentials: 'include' });
      if (res.status === 401 && onUnauthorized) {
        onUnauthorized();
      }
      // 200 → Session ist gültig, 401 war wahrscheinlich eine Race. Nichts tun.
    } catch {
      // Netzwerkfehler → nicht ausloggen, User kann retry'en
    } finally {
      verifyInFlight = null;
    }
  })();
  return verifyInFlight;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, body, query),
  put: <T>(path: string, body?: unknown, query?: Query) => request<T>('PUT', path, body, query),
  delete: <T>(path: string, query?: Query) => request<T>('DELETE', path, undefined, query),
};

/**
 * Liefert die Fehlermeldung aus einem unbekannten Error. ApiError extends Error,
 * ein extra `instanceof ApiError`-Branch ist also redundant.
 *
 * Pattern wurde vorher in 30+ Stellen inline dupliziert — teilweise mit
 * unsicherem `(err as Error).message`-Cast bei Non-Error-Throws.
 */
export function errorMessage(err: unknown): string | undefined {
  if (!(err instanceof Error)) return undefined;
  if (err instanceof ApiError && err.code === 'VALIDATION_ERROR') {
    const grund = validierungsGruende(err.details);
    if (grund) return `${err.message}: ${grund}`;
  }
  return err.message;
}

/** Wie viele Verstoesse die Meldung nennt, bevor der Rest nur noch gezaehlt wird. */
const MAX_VALIDIERUNGSGRUENDE = 5;

/**
 * Das Backend antwortet auf einen Validierungsfehler mit `details`: je Verstoss `{ field, message }`
 * (middleware/error-handler.ts). Ohne sie bliebe nur die Oberzeile "Ungültige Eingabedaten" uebrig, eine
 * Sackgasse fuer jede Maske. Die Meldung gibt wieder, was das Backend sagt, ohne es zu deuten.
 */
function validierungsGruende(details: unknown): string | undefined {
  if (!Array.isArray(details)) return undefined;
  const eintraege = details.flatMap((d) => {
    if (typeof d !== 'object' || d === null) return [];
    const { field, message } = d as { field?: unknown; message?: unknown };
    if (typeof message !== 'string' || !message) return [];
    return [typeof field === 'string' && field ? `${field}: ${message}` : message];
  });
  if (eintraege.length === 0) return undefined;
  const genannt = eintraege.slice(0, MAX_VALIDIERUNGSGRUENDE).join('; ');
  const rest = eintraege.length - MAX_VALIDIERUNGSGRUENDE;
  return rest > 0 ? `${genannt} (${rest} weitere)` : genannt;
}
